import {
    CSSProperties,
    FC,
    PointerEvent,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import styles from './LetterPicker.module.scss';

const BOTTOM_GUTTER = 24;
const MIN_LETTER_FONT_SIZE = 8;
const MAX_LETTER_FONT_SIZE = 14;
const LETTER_HEIGHT_TO_FONT_SIZE_RATIO = 1.45;
// Keeps the bubble clear of the finger and inside the screen.
const BUBBLE_SIZE = 56;
const BUBBLE_GAP_X = 12;
const BUBBLE_OFFSET_Y = 64;
const BUBBLE_MIN_TOP = 8;

type StripRect = { top: number; height: number; left: number };

type LetterPickerProps = {
    letters: string[];
    onSelect: (letter: string, behavior?: ScrollBehavior) => void;
    facetHeaderHeight?: number;
    constrainToViewport?: boolean;
    isActive?: boolean;
};

export const LetterPicker: FC<LetterPickerProps> = ({
    letters,
    onSelect,
    facetHeaderHeight = 0,
    constrainToViewport = false,
    isActive = true,
}) => {
    const lastLetterRef = useRef<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [constrainedHeight, setConstrainedHeight] = useState<number>();
    const [activeLetter, setActiveLetter] = useState<string | null>(null);
    const [bubbleLetter, setBubbleLetter] = useState<string | null>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const bubblePositionRef = useRef({ top: 0, left: 0 });
    // Measured once per scrub, so moves never force a layout while the list is scrolling.
    const stripRectRef = useRef<StripRect | null>(null);

    const positionBubble = useCallback(() => {
        const { top, left } = bubblePositionRef.current;

        if (bubbleRef.current) {
            bubbleRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        }
    }, []);

    // Applies the position before paint when the bubble first mounts.
    useLayoutEffect(positionBubble, [bubbleLetter, positionBubble]);

    useLayoutEffect(() => {
        if (!constrainToViewport || !isActive) return;

        const measure = () => {
            const node = containerRef.current;
            if (!node) return;

            const top = node.getBoundingClientRect().top;
            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const height = Math.max(0, Math.round(viewportHeight - top - BOTTOM_GUTTER));

            setConstrainedHeight((prev) => (prev === height ? prev : height));
        };

        measure();
        window.addEventListener('resize', measure);

        // The bubble moves through its own style attribute while scrubbing, which must not trigger a re-measure.
        const styleObserver = new MutationObserver((records) => {
            if (records.every((record) => record.target === bubbleRef.current)) return;

            measure();
        });
        styleObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['style'],
            subtree: true,
        });

        return () => {
            window.removeEventListener('resize', measure);
            styleObserver.disconnect();
        };
    }, [constrainToViewport, isActive, facetHeaderHeight, letters.length]);

    const selectLetterAtPoint = useCallback(
        (clientY: number) => {
            const rect = stripRectRef.current;

            if (!rect || !rect.height || !letters.length) return;

            // Maps the finger's height over the whole strip to a letter, so there are no dead gaps between letters.
            const ratio = (clientY - rect.top) / rect.height;
            const index = Math.min(
                letters.length - 1,
                Math.max(0, Math.floor(ratio * letters.length)),
            );
            const letter = letters[index];

            bubblePositionRef.current = {
                top: Math.max(BUBBLE_MIN_TOP, clientY - BUBBLE_OFFSET_Y),
                left: Math.max(BUBBLE_MIN_TOP, rect.left - BUBBLE_SIZE - BUBBLE_GAP_X),
            };
            positionBubble();
            setBubbleLetter(letter);

            if (letter !== lastLetterRef.current) {
                lastLetterRef.current = letter;
                setActiveLetter(letter);
                onSelect(letter, 'smooth');
            }
        },
        [letters, onSelect, positionBubble],
    );

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (!constrainToViewport) return;

        // Stops native focus, which would otherwise show its own stuck :focus-visible highlight.
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);

        const { top, height, left } = event.currentTarget.getBoundingClientRect();

        stripRectRef.current = { top, height, left };
        lastLetterRef.current = null;
        selectLetterAtPoint(event.clientY);
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!constrainToViewport || event.buttons === 0) return;

        selectLetterAtPoint(event.clientY);
    };

    const handlePointerEnd = () => {
        stripRectRef.current = null;
        setBubbleLetter(null);
    };

    if (!letters.length) return null;

    const letterFontSize =
        constrainToViewport && constrainedHeight
            ? Math.min(
                  MAX_LETTER_FONT_SIZE,
                  Math.max(
                      MIN_LETTER_FONT_SIZE,
                      constrainedHeight / letters.length / LETTER_HEIGHT_TO_FONT_SIZE_RATIO,
                  ),
              )
            : undefined;

    return (
        <div
            ref={containerRef}
            className={clsx(styles.picker, { [styles.pickerConstrained]: constrainToViewport })}
            style={
                {
                    '--facet-header-height': `${facetHeaderHeight}px`,
                    ...(constrainToViewport && constrainedHeight
                        ? { height: `${constrainedHeight}px` }
                        : {}),
                    ...(letterFontSize ? { '--letter-font-size': `${letterFontSize}px` } : {}),
                } as CSSProperties
            }
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
        >
            {letters.map((letter) => (
                <ResetButton
                    key={letter}
                    data-letter={letter}
                    className={clsx(styles.letter, {
                        [styles.letterActive]: constrainToViewport && letter === activeLetter,
                    })}
                    onClick={() => onSelect(letter)}
                >
                    {letter}
                </ResetButton>
            ))}

            {bubbleLetter &&
                createPortal(
                    <div ref={bubbleRef} className={styles.bubble} aria-hidden="true">
                        {bubbleLetter}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
